import { initializeApp } from 'firebase/app';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import { getAuth, RecaptchaVerifier, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'; // Import Functions

// Enable detailed logs to debug production issues
// setLogLevel is not exported directly from firebase/auth in newer SDKs or modular style in the way expected
// It is usually available on the Auth instance or not needed as much. 
// We will rely on console logs.
// setLogLevel('debug'); 

// Temporarily commented out App Check imports - will re-enable after phone auth is fixed
// import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAt6zoGiyVxsVPMg2bdrxOgwNKoNoh4au4",
  authDomain: "bookstadium-49c57.firebaseapp.com",
  projectId: "bookstadium-49c57",
  storageBucket: "bookstadium-49c57.firebasestorage.app",
  messagingSenderId: "820259137661",
  appId: "1:820259137661:web:543a013b02e06db9d82914"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase App Check for production security
// TEMPORARILY DISABLED to fix phone auth reCAPTCHA issue
// App Check reCAPTCHA v3 can interfere with Phone Auth reCAPTCHA v2
// TODO: Re-enable after phone auth is working, and get separate reCAPTCHA keys
/*
if (typeof window !== 'undefined') {
  if (process.env.NODE_ENV === 'production') {
    const RECAPTCHA_SITE_KEY = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Test key
    
    try {
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true
      });
      console.log('✅ Firebase App Check initialized');
    } catch (error) {
      console.warn('⚠️ App Check initialization failed:', error);
    }
  } else {
    if (typeof self !== 'undefined') {
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
  }
}
*/

// Initialize services
export const db = getFirestore(app);
export const auth = getAuth(app);
// Set language to default device language to avoid region mismatches
auth.useDeviceLanguage();

// ENFORCE TEST MODE ON LOCALHOST
// This allows test numbers to work without reCAPTCHA and without "auth/internal-error"
// Real SMS will not be sent in this mode, but test numbers defined in Firebase Console will work immediately.
if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  auth.settings.appVerificationDisabledForTesting = true;
  console.log("⚠️ Phone Auth: Test mode enabled (reCAPTCHA disabled for localhost)");
}

export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1'); // Initialize Functions

// Connect to emulator in development
if (process.env.NODE_ENV === 'development') {
  console.log("Using Firebase Functions Emulator");
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

// --- NEW GOOGLE AUTH PROVIDER ---
export const googleProvider = new GoogleAuthProvider();

// --- NEW GOOGLE SIGN-IN FUNCTION ---
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    // After sign-in, create their profile in our database
    await createUserProfile(user.uid, {
      name: user.displayName,
      email: user.email,
      photoURL: user.photoURL,
    });
    
    return user;
  } catch (error) {
    console.error("Error signing in with Google:", error);
    throw error;
  }
};

// --- NEW USER PROFILE FUNCTION ---
// This function creates/updates a user's data in the 'users' collection
export const createUserProfile = async (uid, data) => {
  const userDocRef = doc(db, 'users', uid);
  const snapshot = await getDoc(userDocRef);

  if (!snapshot.exists()) {
    // New user: Create profile with their details
    await setDoc(userDocRef, {
      ...data,
      createdAt: new Date().toISOString(),
      // phoneNumber will be added later
    });
  } else {
    // Existing user: Just update their details (merge, don't overwrite)
    await setDoc(userDocRef, data, { merge: true });
  }
};

// --- SIMPLIFIED reCAPTCHA FUNCTION (WORKING VERSION) ---
export const setupRecaptcha = (containerId) => {
  // If we are in test mode (localhost), we don't strictly need a reCAPTCHA, 
  // but we provide a dummy one or a real one that just auto-resolves if needed.
  // Actually, even in test mode, having the verifier object is often required by the SDK signature.
  
  // Clean up existing verifier
  if (window.recaptchaVerifier) {
    try {
      window.recaptchaVerifier.clear();
    } catch (err) {
      console.warn("Error clearing existing reCAPTCHA:", err);
    }
    window.recaptchaVerifier = null;
  }
  
  // Ensure the container exists in the DOM
  // If containerId is a string, get element. If it's already an element (HTMLDivElement), use it.
  const recaptchaContainer = (typeof containerId === 'string') 
    ? document.getElementById(containerId)
    : containerId;

  if (!recaptchaContainer) {
    // If global container is missing, try to create it dynamically as a fallback
    if (typeof containerId === 'string' && containerId === 'global-recaptcha-container') {
       console.warn("Global recaptcha container not found, creating it.");
       const div = document.createElement('div');
       div.id = 'global-recaptcha-container';
       document.body.appendChild(div);
       return setupRecaptcha(containerId); // Retry
    }
    throw new Error(`reCAPTCHA container with id "${containerId}" not found in DOM.`);
  }
  
  // Clear any existing content
  recaptchaContainer.innerHTML = '';
  
  // Create new verifier with explicit render
  try {
    // CLEAR any existing verifier instance on the window object first, if it wasn't null
    if (window.recaptchaVerifier) {
       try {
         window.recaptchaVerifier.clear();
       } catch(e) {/* ignore */}
    }

    const verifier = new RecaptchaVerifier(auth, containerId, {
      'size': 'invisible',
      'callback': (response) => {
        console.log("✅ reCAPTCHA solved successfully");
      },
      'expired-callback': () => {
        console.warn("⚠️ reCAPTCHA expired, please try again");
        // Optionally reset
        if (window.recaptchaVerifier) {
            window.recaptchaVerifier.reset().catch(console.error);
        }
      }
    });

    window.recaptchaVerifier = verifier;
    
    // Explicitly render the reCAPTCHA
    // We don't await here to keep the function synchronous, but we log the result
    verifier.render().then((widgetId) => {
      window.recaptchaWidgetId = widgetId;
      console.log("✅ reCAPTCHA rendered with widget ID:", widgetId);
    }).catch((error) => {
      console.error("❌ reCAPTCHA render error:", error);
    });
    
    console.log("✅ reCAPTCHA verifier created");
    return verifier;
  } catch (error) {
    console.error("❌ Error creating reCAPTCHA verifier:", error);
    throw error;
  }
};